// Navigation.js
import React from 'react';
import { Link, useLocation } from 'react-router-dom'; 
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ColorModeToggle from '@/components/ui/button/ColorModeToggle';
import useLogo from '@/hooks/useLogo';
import ClientSelector from '@/components/ui/client-selector/ClientSelector';
import { useAuth } from '@/lib/firebase/auth'; 
import { useClient } from '@/stores/ClientContext';

const Navigation = React.memo(({ toggleLeftDrawer, toggleRightDrawer, pageTitle }) => {
  const theme = useTheme();
  const logoUrl = useLogo();
  const location = useLocation();
  const { userDetails } = useAuth();
  const { availableClients } = useClient();

  const isAdminPath = location.pathname.startsWith('/epl-admin');
  const isSettingsPath = location.pathname.startsWith('/settings');

  return (
    <AppBar
      position="static"
      sx={{
        height: 48,
        backgroundColor: theme.palette.appBar.main,
        color: theme.palette.appBar.contrastText,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#2F3338' : 'transparent'}`,
      }}
    >
      <Toolbar variant="dense">
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleLeftDrawer}
          sx={{ mr: 1 }}
        >
          <MenuIcon />
        </IconButton>
        <Box
          component={Link}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <Box
            component="img"
            sx={{ height: 34, width: 34 }}
            alt="App Logo"
            src={logoUrl}
          />
        </Box>

        {!isAdminPath && !isSettingsPath && availableClients.length > 0 && (
          <Box sx={{ ml: 2 }}>
            <ClientSelector />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography
            component={Link}
            to="/"
            variant="h6"
            sx={{
              fontSize: '1rem',
              textTransform: 'lowercase',
              fontFamily: 'Monospace, sans-serif',
              color: theme.palette.appBar.contrastText,
              textDecoration: 'none',
              paddingRight: '4px',
            }}
          >
            {/* hermes */}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              textTransform: 'lowercase',
              fontFamily: 'Monospace, sans-serif',
              color: theme.palette.appBar.contrastText,
              paddingLeft: '4px',
              paddingRight: '8px',
            }}
          >
            /
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              textTransform: 'lowercase',
              fontFamily: 'Monospace, sans-serif',
              color: theme.palette.appBar.contrastText,
            }}
          >
            {pageTitle}
          </Typography>
        </Box>
        <ColorModeToggle />
        <IconButton
          edge="end"
          color="inherit"
          aria-label="profile"
          onClick={toggleRightDrawer}
        >
          {userDetails?.profilePicture ? (
            <Avatar alt="Profile Picture" src={userDetails.profilePicture} sx={{ width: 32, height: 32 }} />
          ) : (
            <AccountCircle />
          )}
        </IconButton>
      </Toolbar>
    </AppBar>
  );
});

export default Navigation;
